/**
 * Tenant-timezone helpers shared by every calendar view (11E.7).
 *
 * Extracted from CalendarPage when Month and Day views were added — all
 * three views MUST bucket sessions with the same wall-clock arithmetic or
 * a chip lands in one view and vanishes in another.
 *
 * The invariant (ADR 0005, frontend half): every user-visible date
 * computation runs in the tenant's timezone. Sessions are stored and
 * transported as UTC instants; the browser's own timezone is never an
 * input to any of these functions.
 */

import { fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Return a Date whose getFullYear/Month/Date/Hours read as the wall clock
 * in `tz` for the same instant as `d`.
 */
export function inTenantTz(d: Date, tz: string): Date {
  return toZonedTime(d, tz);
}

/** Combine a tenant-tz wall-clock (Y/M/D + hour) into a UTC `Date`. */
export function tenantWallClockToUtc(
  year: number,
  month0: number,
  day: number,
  hour: number,
  tz: string,
): Date {
  return fromZonedTime(new Date(year, month0, day, hour, 0, 0, 0), tz);
}

/** ISO date (`YYYY-MM-DD`) from an already-tenant-tz Date object. */
export function fmtIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Start of ISO week (Monday) for a Date already projected into a tenant tz. */
export function startOfIsoWeekInTz(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const day = out.getDay(); // Sun=0..Sat=6
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  return out;
}

/** First day of the month containing `d` (wall-clock). */
export function startOfMonthInTz(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

/** Add N days to a Date treated as a tz-local wall clock. */
export function addDays(d: Date, n: number): Date {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + n,
    d.getHours(),
    d.getMinutes(),
    0,
    0,
  );
}

/** Add N months to a Date treated as a tz-local wall clock. */
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1, 0, 0, 0, 0);
}

export function sameWallClockDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The 42-cell (6x7) grid a month view renders: leading days from the
 * previous month, the month itself, then trailing days — always starting
 * on Monday to match `startOfIsoWeekInTz`.
 *
 * Fixed at 6 rows rather than a variable 4–6 so the grid doesn't reflow
 * height as the operator pages through months.
 */
export function monthGridDates(monthStart: Date): Date[] {
  const gridStart = startOfIsoWeekInTz(monthStart);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/** Format a UTC instant as `HH:MM` wall-clock in the tenant timezone. */
export function fmtTimeInTz(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
